"use client";

import { useEffect } from 'react';
import { useConnectRequest } from '../contexts/ConnectRequestContext';
import { getAdminSocket } from '@/services/socket';
import authService from '@/services/authService';
import { Send, X, Phone, Mail } from 'lucide-react';
import styles from './ConnectRequestModal.module.css';
import { useRouter } from 'next/navigation';

export default function ConnectRequestModal() {
  const { connectRequest, setConnectRequest, greetingMessage, setGreetingMessage } = useConnectRequest();
  const router = useRouter();

  useEffect(() => {
    if (!connectRequest) return;

    // Lấy thông tin admin để tạo tin nhắn mặc định
    const loadAdminInfo = async () => {
      try {
        const admin = await authService.getCurrentAdmin();
        if (admin) {
          setGreetingMessage(`Chào, tôi là ${admin.fullName || 'Admin'}, có thể hỗ trợ gì cho bạn?`);
        } else {
          setGreetingMessage('Chào, tôi là Admin, có thể hỗ trợ gì cho bạn?');
        }
      } catch {
        setGreetingMessage('Chào, tôi là Admin, có thể hỗ trợ gì cho bạn?');
      }
    };

    loadAdminInfo();
  }, [connectRequest, setGreetingMessage]);

  const handleCancelConnect = () => {
    setConnectRequest(null);
    setGreetingMessage('');
  };

  const handleConnectAndSend = () => {
    if (!connectRequest) return;
    
    try {
      const token = authService.getToken();
      if (!token) {
        alert('Phiên đăng nhập admin đã hết hạn. Vui lòng đăng nhập lại.');
        router.push('/admin/login');
        return;
      }

      const socket = getAdminSocket({ tokenOverride: token });

      const text = greetingMessage.trim();
      if (!text) {
        alert('Vui lòng nhập tin nhắn chào');
        return;
      }

      // Join room trước
      socket.emit('admin_join_room', connectRequest.roomId);
      
      // Đợi một chút để đảm bảo room đã được join
      setTimeout(() => {
        // Gửi tin nhắn chào
        socket.emit('admin_send_message', { roomId: connectRequest.roomId, message: text });
        
        // Chuyển đến trang chat
        router.push('/admin/chat');
      }, 300);

      // Đóng modal
      handleCancelConnect();
    } catch (error) {
      console.error('Error connecting to chat:', error);
      alert('Có lỗi xảy ra. Vui lòng thử lại.');
    }
  };

  if (!connectRequest) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleCancelConnect}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Yêu cầu kết nối từ khách hàng</h3>
          <button className={styles.modalClose} onClick={handleCancelConnect}>
            <X size={20} />
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.modalUserInfo}>
            <p><strong>Khách hàng:</strong> {connectRequest.user?.name || 'Khách'}</p>
            {connectRequest.user?.phone && (
              <p>
                <Phone size={14} style={{ marginRight: 6 }} />
                {connectRequest.user.phone}
              </p>
            )}
            {connectRequest.user?.email && (
              <p>
                <Mail size={14} style={{ marginRight: 6 }} />
                {connectRequest.user.email}
              </p>
            )}
          </div>
          <div className={styles.modalInputGroup}>
            <label>Tin nhắn chào (sẽ gửi đến khách hàng):</label>
            <textarea
              className={styles.modalTextarea}
              value={greetingMessage}
              onChange={(e) => setGreetingMessage(e.target.value)}
              placeholder="Chào, tôi là Admin, có thể hỗ trợ gì cho bạn?"
              rows={4}
            />
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.modalBtnCancel} onClick={handleCancelConnect}>
            Hủy
          </button>
          <button 
            className={styles.modalBtnSend} 
            onClick={handleConnectAndSend} 
            disabled={!greetingMessage.trim()}
          >
            <Send size={16} style={{ marginRight: 6 }} />
            Kết nối và gửi
          </button>
        </div>
      </div>
    </div>
  );
}

